import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const AVATAR_OPTIONS = {
  topType: [
    'LongHairStraight',    // Common female
    'LongHairBob',         // Common female
    'ShortHairShortFlat',  // Common male
    'ShortHairShortWaved', // Common male
    'LongHairBun',         // Common female
    'ShortHairSides',      // Common male
    'NoHair',              // Universal
    'WinterHat1'           // Accessory
  ],
  accessoriesType: [
    'Blank',           // None
    'Prescription01',  // Common glasses
    'Prescription02',  // Common glasses
    'Round',          // Classic glasses
    'Sunglasses',     // Popular
    'Wayfarers',      // Classic sunglasses
    'Kurt',           // Round sunglasses
    'Prescription03'  // Modern glasses
  ],
  hairColor: [
    'Black',
    'Brown',
    'BrownDark',
    'BlondeGolden',
    'Auburn',
    'Platinum',
    'Red',
    'SilverGray'
  ],
  facialHairType: [
    'Blank',          // None
    'BeardLight',     // Light beard
    'BeardMedium',    // Medium beard
    'BeardMajestic',  // Full beard
    'MoustacheFancy', // Styled mustache
    'MoustacheMagnum',// Full mustache
    'Goatee',         // Goatee
    'BeardScruff'     // Stubble
  ],
  clotheType: [
    'BlazerShirt',     // Professional
    'ShirtCrewNeck',   // Casual
    'ShirtScoopNeck',  // Casual
    'ShirtVNeck',      // Casual
    'Hoodie',          // Casual
    'BlazerSweater',   // Smart casual
    'CollarSweater',   // Smart casual
    'GraphicShirt'     // Casual
  ],
  clotheColor: [
    'Black',
    'Blue01',
    'Blue02',
    'Gray01',
    'Gray02',
    'White',
    'Red',
    'PastelBlue'
  ],
  eyeType: [
    'Default',
    'Happy',
    'Side',
    'Wink',
    'Squint',
    'Simple',
    'Hearts',
    'Surprised'
  ],
  eyebrowType: [
    'Default',
    'DefaultNatural',
    'RaisedExcited',
    'Angry',
    'SadConcerned',
    'UpDown',
    'FlatNatural',
    'RaisedExcitedNatural'
  ],
  mouthType: [
    'Default',
    'Smile',
    'Serious',
    'Twinkle',
    'Tongue',
    'Concerned',
    'Happy',
    'Grin'
  ],
  skinColor: [
    'Light',
    'Pale',
    'Tanned',
    'Brown',
    'DarkBrown',
    'Black',
    'Yellow',
    'Asian'
  ]
};

// Helper function to build avatar URL from options
const buildAvatarUrl = (options) => {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    params.append(key, value);
  });
  return `https://avataaars.io/?${params.toString()}`;
};

// Helper function to generate random options
const makeRandomOptions = () => {
  const randomOption = (options) => options[Math.floor(Math.random() * options.length)];
  
  return {
    topType: randomOption(AVATAR_OPTIONS.topType),
    accessoriesType: randomOption(AVATAR_OPTIONS.accessoriesType),
    hairColor: randomOption(AVATAR_OPTIONS.hairColor),
    facialHairType: randomOption(AVATAR_OPTIONS.facialHairType),
    clotheType: randomOption(AVATAR_OPTIONS.clotheType),
    clotheColor: randomOption(AVATAR_OPTIONS.clotheColor),
    eyeType: randomOption(AVATAR_OPTIONS.eyeType),
    eyebrowType: randomOption(AVATAR_OPTIONS.eyebrowType),
    mouthType: randomOption(AVATAR_OPTIONS.mouthType),
    skinColor: randomOption(AVATAR_OPTIONS.skinColor)
  };
};

// Custom Dropdown Component
const CustomDropdown = ({ label, value, options, onChange, formatDisplayName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const selectedDisplayName = formatDisplayName ? formatDisplayName(value) : value;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-sm font-medium text-[#1a1a1a] mb-1 sm:mb-2 capitalize">
        {label}
      </label>
      
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full max-w-xs border border-gray-200 rounded-lg p-2 sm:p-2.5 text-[#1a1a1a] focus:outline-none focus:border-primary text-sm md:text-base bg-white text-left flex items-center justify-between hover:border-gray-300 transition-colors"
      >
        <span className="truncate">{selectedDisplayName}</span>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full max-w-xs mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((option) => {
            const displayName = formatDisplayName ? formatDisplayName(option) : option;
            const isSelected = option === value;
            
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                  isSelected 
                    ? 'bg-primary text-white hover:bg-primaryDark' 
                    : 'text-[#1a1a1a]'
                }`}
              >
                {displayName}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function AvatarCreatorModal({ show, onClose, onSave }) {
  const [avatarOptions, setAvatarOptions] = useState({
    topType: 'LongHairStraight', // Default to a female hairstyle
    accessoriesType: 'Blank',
    hairColor: 'Brown',
    facialHairType: 'Blank',
    clotheType: 'BlazerShirt',
    clotheColor: 'Blue01',
    eyeType: 'Default',
    eyebrowType: 'Default',
    mouthType: 'Smile',
    skinColor: 'Light'
  });

  const [previewUrl, setPreviewUrl] = useState('');

  // Update preview URL whenever options change
  useEffect(() => {
    const newUrl = buildAvatarUrl(avatarOptions);
    console.log('New preview URL:', newUrl);
    setPreviewUrl(newUrl);
  }, [avatarOptions]);

  const handleChange = (key, value) => {
    console.log('Changing', key, 'to', value);
    setAvatarOptions(prev => {
      const newOptions = { ...prev, [key]: value };
      return newOptions;
    });
  };

  const generateRandom = () => {
    const newOptions = makeRandomOptions();
    console.log('Generated random options:', newOptions);
    setAvatarOptions(newOptions);
  };

  const handleSave = () => {
    onSave({
      url: previewUrl,
      options: avatarOptions
    });
    onClose();
  };

  // Helper function to format option display names
  const formatDisplayName = (option) => {
    if (option === 'Blank') return 'None';
    
    // Special cases for specific types
    const specialCases = {
      // Top Type
      'LongHairStraight': 'Long Straight',
      'LongHairBob': 'Bob Cut',
      'ShortHairShortFlat': 'Short Flat',
      'ShortHairShortWaved': 'Short Wavy',
      'LongHairBun': 'Hair Bun',
      'ShortHairSides': 'Short Sides',
      'NoHair': 'Bald',
      'WinterHat1': 'Winter Hat',
      
      // Accessories
      'Prescription01': 'Classic Glasses',
      'Prescription02': 'Square Glasses',
      'Prescription03': 'Round Glasses',
      'Kurt': 'Retro Glasses',
      'Wayfarers': 'Wayfarers',
      
      // Colors
      'Blue01': 'Light Blue',
      'Blue02': 'Navy Blue',
      'Gray01': 'Light Gray',
      'Gray02': 'Dark Gray',
      'PastelBlue': 'Pastel Blue',
      'BlondeGolden': 'Golden Blonde',
      'BrownDark': 'Dark Brown',
      
      // Facial Hair
      'BeardLight': 'Light Beard',
      'BeardMedium': 'Medium Beard',
      'BeardMajestic': 'Full Beard',
      'MoustacheFancy': 'Fancy Mustache',
      'MoustacheMagnum': 'Full Mustache',
      'BeardScruff': 'Stubble',
      
      // Clothes
      'BlazerShirt': 'Blazer',
      'ShirtCrewNeck': 'Crew Neck',
      'ShirtScoopNeck': 'Scoop Neck',
      'ShirtVNeck': 'V-Neck',
      'BlazerSweater': 'Sweater Blazer',
      'CollarSweater': 'Collared Sweater',
      'GraphicShirt': 'Graphic Tee',
      
      // Eyebrows
      'DefaultNatural': 'Natural',
      'RaisedExcited': 'Raised',
      'RaisedExcitedNatural': 'Naturally Raised',
      'FlatNatural': 'Flat',
      'SadConcerned': 'Concerned',
      'UpDown': 'Expressive',
    };

    return specialCases[option] || option.replace(/([A-Z])/g, ' $1').trim();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 max-w-2xl w-full shadow-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4 sm:mb-6 flex-shrink-0">
          <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a1a]">Create Your Avatar</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 md:gap-8 flex-1 min-h-0">
          {/* Avatar Preview */}
          <div className="flex-shrink-0 w-full lg:w-[300px]">
            <div className="aspect-square rounded-lg bg-[#f8f9fb] mb-3 sm:mb-4 flex items-center justify-center p-4 sm:p-8">
              <img 
                src={previewUrl}
                alt="Avatar preview"
                className="w-full h-full"
                key={previewUrl} // Force re-render when URL changes
              />
            </div>
            <button
              onClick={generateRandom}
              className="w-full py-2 sm:py-2.5 bg-[#f8f9fb] text-[#1a1a1a] rounded-lg hover:bg-gray-100 transition text-sm font-medium"
            >
              Generate Random
            </button>
          </div>

          {/* Customization Options */}
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-3 sm:gap-x-4 gap-y-2 sm:gap-y-3 overflow-y-auto min-h-0">
            {Object.entries(AVATAR_OPTIONS).map(([key, options]) => {
              const label = key
                .replace(/([A-Z])/g, ' $1')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');

              return (
                <CustomDropdown
                  key={key}
                  label={label}
                  value={avatarOptions[key]}
                  options={options}
                  onChange={(value) => handleChange(key, value)}
                  formatDisplayName={formatDisplayName}
                />
              );
            })}
          </div>
        </div>

        <div className="flex justify-end space-x-2 sm:space-x-3 mt-4 sm:mt-6 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 sm:px-4 py-2 bg-primary text-white rounded hover:bg-primaryDark text-sm sm:text-base"
          >
            Save Avatar
          </button>
        </div>
      </div>
    </div>
  );
}

AvatarCreatorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}; 